import React, { useState, useEffect, useRef } from 'react'
import { Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'

import { httpOk, POST, PUT } from 'helpers/api'

function templateItemKey(item) {
	return item?.product_id ?? item?.element_id ?? item?.id
}

function filterItemsBySearch(list, query) {
	if (!query) return [...list]
	const q = query.toLowerCase()
	return list.filter((item) =>
		item?.name?.toString().toLowerCase().includes(q),
	)
}

function removeItemFromList(list, item) {
	const key = templateItemKey(item)
	return list.filter((row) => templateItemKey(row) !== key)
}

function addItemToList(list, item) {
	const key = templateItemKey(item)
	if (list.some((row) => templateItemKey(row) === key)) return [...list]
	const next = [...list, item]
	next.sort((a, b) => (templateItemKey(a) ?? 0) - (templateItemKey(b) ?? 0))
	return next
}

function Index({
	products,
	modalTemplateCRUD,
	setModalTemplateCRUD,
	toggleModalTemplateCRUD,
	onSaved,
}) {
	const { t } = useTranslation()

	const [items, setItems] = useState([])
	const [itemsDuplicate, setItemsDuplicate] = useState([])
	const [searchQuery, setSearchQuery] = useState('')
	const listsInitialized = useRef(false)
	const modalWasOpenRef = useRef(false)
	const initialSelectedRef = useRef([])

	const isEditMode = modalTemplateCRUD.id != null && modalTemplateCRUD.id !== ''

	async function createUpdate() {
		if (!String(modalTemplateCRUD.name ?? '').trim()) {
			return
		}
		const body = {
			name: modalTemplateCRUD.name,
			items: modalTemplateCRUD.products,
		}
		const response = isEditMode
			? await PUT(`/documents-templates/${modalTemplateCRUD.id}`, body, { loader: true })
			: await POST('/documents-templates', body, { loader: true })

		if (httpOk(response)) {
			toggleModalTemplateCRUD(false)
			if (onSaved) onSaved()
		}
	}

	function refreshLeftList(duplicate, query) {
		setItemsDuplicate(duplicate)
		setItems(filterItemsBySearch(duplicate, query))
	}

	function addAllToList() {
		const toMove = [...items]
		if (toMove.length === 0) return

		let duplicate = itemsDuplicate
		toMove.forEach((row) => {
			duplicate = removeItemFromList(duplicate, row)
		})

		refreshLeftList(duplicate, searchQuery)
		setModalTemplateCRUD({
			...modalTemplateCRUD,
			products: [...modalTemplateCRUD.products, ...toMove],
		})
	}

	function deleteAllItem() {
		let restored = [...itemsDuplicate]
		modalTemplateCRUD.products.forEach((row) => {
			restored = addItemToList(restored, row)
		})

		refreshLeftList(restored, searchQuery)
		setModalTemplateCRUD({ ...modalTemplateCRUD, 'products': [] })
	}

	function addToList(index) {
		const row = items[index]
		if (!row) return

		const duplicate = removeItemFromList(itemsDuplicate, row)
		refreshLeftList(duplicate, searchQuery)
		setModalTemplateCRUD({
			...modalTemplateCRUD,
			products: [...modalTemplateCRUD.products, row],
		})
	}

	function deleteItem(index) {
		const row = modalTemplateCRUD.products[index]
		if (!row) return

		const products = [...modalTemplateCRUD.products]
		products.splice(index, 1)

		const duplicate = addItemToList(itemsDuplicate, row)
		refreshLeftList(duplicate, searchQuery)
		setModalTemplateCRUD({ ...modalTemplateCRUD, 'products': products })
	}

	function search(value) {
		setSearchQuery(value)
		setItems(filterItemsBySearch(itemsDuplicate, value))
	}

	useEffect(() => {
		if (!modalTemplateCRUD.bool) {
			listsInitialized.current = false
			modalWasOpenRef.current = false
			initialSelectedRef.current = []
			setSearchQuery('')
			return
		}

		if (!modalWasOpenRef.current) {
			modalWasOpenRef.current = true
			initialSelectedRef.current = [...(modalTemplateCRUD.products || [])]
		}

		if (listsInitialized.current) return

		if (!products?.length && initialSelectedRef.current.length === 0) {
			setSearchQuery('')
			setItemsDuplicate([])
			setItems([])
			listsInitialized.current = true
			return
		}
		if (!products?.length) return

		let left = [...products]
		initialSelectedRef.current.forEach((row) => {
			left = removeItemFromList(left, row)
		})
		setSearchQuery('')
		setItemsDuplicate(left)
		setItems(left)
		listsInitialized.current = true
	}, [modalTemplateCRUD.bool, products, modalTemplateCRUD.products])

	return (
		<>
			<Modal show={modalTemplateCRUD.bool} animation={false}
 size="xl" centered
				onHide={() => toggleModalTemplateCRUD(false)}>
				<Modal.Header closeButton>
					<Modal.Title>
						{isEditMode ? t('edit') : t('create_template')}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div>
						<div className="form-group">
							<input type="text" className="form-control"
								name="name"
								autoComplete="off"
								placeholder={t('template')}
								value={modalTemplateCRUD.name}
								onChange={(e) => setModalTemplateCRUD({ ...modalTemplateCRUD, 'name': e.target.value })} />
						</div>
						<div className="d-flex gap-3">
							<div className="table-responsive w-100"
								style={{ 'height': '500px', 'overflow': 'auto' }}>
								<table className="table table-bordered mb-0">
									<thead>
										<tr>
											<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
											<th style={{ 'width': '60%' }} className="td-input">
												<input type="text" placeholder={`${t('search')}...`}
													value={searchQuery}
													onChange={(e) => search(e.target.value)} />
											</th>
											<th style={{ 'width': '40%' }}>{t('type')}</th>
											<th style={{ 'minWidth': '33px' }}>
												<div className="d-flex justify-content-end">
													<div className="table-action-button table-action-primary-button"
														onClick={() => addAllToList()}>
														<i className="uil uil-angle-double-right"></i>
													</div>
												</div>
											</th>
										</tr>
									</thead>
									<tbody>
										{items?.map((item, index) => (
											<tr key={templateItemKey(item) ?? index}>
												<td className="text-center">{index + 1}</td>
												<td className="td-ellipsis">{item.name}</td>
												<td className="td-ellipsis">{item.product_type_name}</td>
												<td>
													<div className="d-flex justify-content-end">
														<div className="table-action-button table-action-primary-button"
															onClick={() => addToList(index)}>
															<i className="uil uil-angle-right-b"></i>
														</div>
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
							<div className="table-responsive w-100"
								style={{ 'height': '500px', 'overflow': 'auto' }}>
								<table className="table table-bordered mb-0">
									<thead>
										<tr>
											<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
											<th style={{ 'width': '60%' }}>{t('name2')}</th>
											<th style={{ 'width': '40%' }}>{t('type')}</th>
											<th style={{ 'minWidth': '33px' }}>
												<div className="d-flex justify-content-end">
													<div className="table-action-button table-action-danger-button"
														onClick={() => deleteAllItem()}>
														<i className="uil uil-times"></i>
													</div>
												</div>
											</th>
										</tr>
									</thead>
									<tbody>
										{modalTemplateCRUD?.products?.map((item, index) => (
											<tr key={templateItemKey(item) ?? index}>
												<td className="text-center">{index + 1}</td>
												<td className="td-ellipsis">{item.name}</td>
												<td className="td-ellipsis">{item.product_type_name}</td>
												<td>
													<div className="d-flex justify-content-end">
														<div className="table-action-button table-action-danger-button"
															onClick={() => deleteItem(index)}>
															<i className="uil uil-times"></i>
														</div>
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<div className="d-flex gap-2 w-100">
						<button type="button" className="btn btn-warning w-100"
							onClick={() => toggleModalTemplateCRUD(false)}>
							{t('cancel')}
						</button>
						<button type="button" className="btn btn-primary w-100"
							onClick={() => createUpdate()}>
							{isEditMode ? t('save') : t('create')}
						</button>
					</div>
				</Modal.Footer>
			</Modal>
		</>
	)
}

export default Index
